// Shared helpers for interpreting a raw Tata Smartflo call row. Used by both
// IncomingCalls (per-call table) and AgentSidebar (per-agent rollups) so the
// definition of "answered" stays in exactly one place.

// Options for the "Query Type" dropdown (call-history modal) and the matching
// filter/label everywhere else it's shown — the single source of truth so
// QueryPanel, CallHistoryModal, and IncomingCalls can't drift out of sync.
// Stored as the `value` (not the label) on the call row, so relabeling an
// option here doesn't orphan already-saved data.
export const QUERY_TYPE_OPTIONS = [
  { value: 'new_product', label: 'New Product' },
  { value: 'course_related', label: 'Course Related' },
  { value: 'order_status', label: 'Order Status/Tracking' },
  { value: 'call_not_received', label: 'Call Not Received' },
  { value: 'other', label: 'Other' },
];

export const QUERY_TYPE_LABELS = Object.fromEntries(
  QUERY_TYPE_OPTIONS.map((option) => [option.value, option.label])
);

// Inbound and click-to-call events format the same Indian number differently
// (with/without "+91"), which would otherwise split one customer into two rows.
export const normalizeNumber = (raw) => {
  if (!raw) return raw;
  const digits = raw.replace(/\D/g, '');
  const local = digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits;
  return local.length === 10 ? `+91${local}` : raw;
};

// Detects if a phone number is international (doesn't start with +91)
export const isInternationalNumber = (raw) => {
  if (!raw) return false;
  const normalized = normalizeNumber(raw);
  return !normalized.startsWith('+91');
};

// For click-to-call (agent-initiated) legs, caller_id_number is our own Smartflo
// bridge number and the real customer number is who we dialed (call_to_number).
// For inbound calls it's the reverse: call_to_number is our IVR number.
export const getCustomerNumber = (call) => {
  const direction = (call.direction || '').toLowerCase();
  const raw = direction.includes('click')
    ? call.call_to_number || call.customer_no_with_prefix || call.caller_id_number
    : call.customer_no_with_prefix || call.caller_id_number || call.call_to_number;
  return raw ? normalizeNumber(raw) : '-';
};

// billsec is actual talk time; duration also includes ring/setup time before
// anyone picks up. For click-to-call, answered_agent_number/duration only mean
// the AGENT's own leg connected — the customer can still fail to pick up, in
// which case billsec stays 0. Only billsec > 0 proves the customer actually
// answered and talked.
export const hadTalkTime = (call) => {
  const billsec = Number(call.billsec);
  return !Number.isNaN(billsec) && billsec > 0;
};

// A "call received on server" webhook (fired the instant a call starts
// ringing, before answer/hangup) lands a row with no outcome fields at all —
// without this window it would immediately read as "Missed" below. Past this
// window with still no outcome, it really is stale/missed (safety net for a
// call whose final webhook never arrives).
export const RINGING_WINDOW_MS = 2 * 60 * 1000;

// A call counts as answered only when a live agent picked up AND the customer
// actually talked to them (see hadTalkTime) — this is the strongest signal we
// have, direct proof of a real conversation. An unanswered click-to-call is an
// agent's callback attempt, not a fresh inbound miss — kept as its own
// "Attempted" status so it's not confused with a customer never called back.
// For plain inbound calls (no agent leg at all in this IVR flow), fall back to
// Tata's own call_status/status field when it's present, since it's their
// authoritative classification rather than something we're inferring.
// Calls we placed: click-to-call, and any other outbound leg Tata reports
// (dialer/campaign). Same rule as server/callStatus.js's isOutboundCall.
export const isOutboundCall = (call) => {
  const direction = (call.direction || '').toLowerCase();
  return direction.includes('click') || direction.startsWith('out');
};

const isRingingNow = (call, now) => {
  if (!call.start_stamp) return false;
  const startedAt = new Date(call.start_stamp.replace(' ', 'T')).getTime();
  return !Number.isNaN(startedAt) && now - startedAt < RINGING_WINDOW_MS;
};

export const getCallStatus = (call, now = Date.now()) => {
  const direction = (call.direction || '').toLowerCase();
  const explicitStatus = (call.call_status || call.status || '').toLowerCase();

  // For click-to-call, Tata's call_status is checked first because billsec
  // alone is unreliable here: a call the customer never picked up can still
  // log a few seconds of billsec (the agent's own ring/setup leg), which
  // would otherwise get misread as a real conversation via hadTalkTime.
  if (direction.includes('click')) {
    if (explicitStatus === 'missed') return 'Attempted';
    if (call.answered_agent_number && hadTalkTime(call)) return 'Answered';
    return 'Attempted';
  }

  // Any other call we placed (dialer/campaign): our attempt, like a
  // click-to-call, so unanswered it's Attempted — never a customer's Missed
  // call, which would raise missed-call alerts and the Critical list. Unlike
  // click-to-call, Tata can send these rows while they ring, so the Ringing
  // window still applies.
  if (isOutboundCall(call)) {
    if (explicitStatus === 'missed') return 'Attempted';
    if (call.answered_agent_number && hadTalkTime(call)) return 'Answered';
    if (explicitStatus === 'answered') return 'Answered';
    if (isRingingNow(call, now)) return 'Ringing';
    return 'Attempted';
  }

  if (call.answered_agent_number && hadTalkTime(call)) return 'Answered';

  if (explicitStatus === 'answered') return 'Answered';
  if (explicitStatus === 'missed') return 'Missed';

  if (call.disposition) return call.disposition;

  if (isRingingNow(call, now)) return 'Ringing';

  return 'Missed';
};

// Tata sends start_stamp as "YYYY-MM-DD HH:MM:SS"; format it nicely where we
// can, but fall back to the raw value rather than showing "Invalid Date".
export const formatTimestamp = (raw) => {
  if (!raw) return '-';
  const parsed = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// Follow-up dates come from DateTimePicker (minute precision, no seconds),
// unlike call timestamps from Tata — so formatTimestamp's seconds field
// would always render a meaningless ":00". This mirrors formatTimestamp
// minus seconds, and uses a numeric (non-padded) hour to match the picker's
// own trigger label.
export const formatFollowUpDate = (raw) => {
  if (!raw) return '-';
  const parsed = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Short weekday/date label for grouping a chat thread's calls by day, e.g.
// "Tue, 16 Jul" — distinct from formatTimestamp's full date+time.
export const callDay = (call) => {
  const raw = call.start_stamp;
  if (!raw) return null;
  const parsed = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

// start_stamp arrives as "YYYY-MM-DD HH:MM:SS"; the date portion sorts/compares
// fine as a plain string against <input type="date"> values, no Date parsing needed.
export const callDate = (call) => (call.start_stamp || '').slice(0, 10);

// Day-of-week (0=Sun..6=Sat) and hour-of-day (0-23) for the Peak Hours
// heatmap — same parse idiom as callDay/formatTimestamp.
export const callWeekday = (call) => new Date((call.start_stamp || '').replace(' ', 'T')).getDay();
export const callHour = (call) => new Date((call.start_stamp || '').replace(' ', 'T')).getHours();

export const filterCallsByDate = (calls, fromDate, toDate) => {
  if (!fromDate && !toDate) return calls;
  return calls.filter((call) => {
    const date = callDate(call);
    if (!date) return false;
    if (fromDate && date < fromDate) return false;
    if (toDate && date > toDate) return false;
    return true;
  });
};

// Local YYYY-MM-DD (not UTC) so "Today" lines up with the browser's calendar
// day — toISOString() would roll over early/late near midnight in IST.
export const localDateStr = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// n=1 -> today, n=7 -> today and the 6 days before it, etc. Shared date-range
// preset math for any page with a "Last N Days" filter (Trends, My Dashboard).
export const daysAgo = (n) => localDateStr(new Date(Date.now() - (n - 1) * 24 * 60 * 60 * 1000));

const dayLabel = (dateStr) =>
  new Date(`${dateStr}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

// One entry per calendar day in [start, end], zero-filled — so a quiet day
// shows as a dip in the line rather than a gap in the data.
export const buildDailyPoints = (calls, start, end) => {
  const byDate = new Map();
  for (let cursor = new Date(`${start}T00:00:00`); cursor <= new Date(`${end}T00:00:00`); cursor.setDate(cursor.getDate() + 1)) {
    const date = localDateStr(cursor);
    byDate.set(date, { date, label: dayLabel(date), answered: 0, missed: 0, attempted: 0 });
  }

  for (const call of calls) {
    const date = callDate(call);
    const bucket = byDate.get(date);
    if (!bucket) continue;
    const status = getCallStatus(call);
    if (status === 'Answered') bucket.answered += 1;
    else if (status === 'Missed') bucket.missed += 1;
    else if (status === 'Attempted') bucket.attempted += 1;
  }

  return Array.from(byDate.values());
};

// 7 (day, 0=Sun) x 24 (hour) call-count grid for the Peak Hours heatmap.
// Click-to-call legs are agent-initiated (outgoing), not a customer calling
// in, so they're excluded to keep this a true picture of incoming call volume.
export const buildHeatmapGrid = (calls) => {
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  for (const call of calls) {
    if (!call.start_stamp) continue;
    if ((call.direction || '').toLowerCase().includes('click')) continue;
    grid[callWeekday(call)][callHour(call)] += 1;
  }
  return grid;
};

// Same mm:ss shape as formatDuration, but averaged across a set of calls'
// billsec totals (e.g. one agent's answered calls) rather than a single call.
export const formatAvgDuration = (totalBillsec, answeredCount) => {
  if (!answeredCount) return '-';
  const avg = Math.round(totalBillsec / answeredCount);
  const minutes = Math.floor(avg / 60);
  const seconds = String(avg % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

export const formatDuration = (call) => {
  const seconds = call.billsec || call.duration;
  if (!seconds) return '-';
  const total = Number(seconds);
  if (Number.isNaN(total)) return seconds;
  const minutes = Math.floor(total / 60);
  const remaining = String(total % 60).padStart(2, '0');
  return `${minutes}:${remaining}`;
};

// Collapses repeat calls from the same customer into one row: the row reflects
// the most recent call (so a callback that got answered overrides earlier
// missed attempts). Relies on `calls` already being sorted newest-first.
export const groupByCustomer = (calls) => {
  const groups = [];
  const byNumber = new Map();
  for (const call of calls) {
    const number = getCustomerNumber(call);
    let group = byNumber.get(number);
    if (!group) {
      group = { customerNumber: number, latestCall: call, callsNewestFirst: [] };
      byNumber.set(number, group);
      groups.push(group);
    }
    group.callsNewestFirst.push(call);
  }
  return groups;
};

// Per-agent rollup (calls handled, answered, talk time) — shared by the
// Agent Details table and the Trends "Agent Comparison" chart so both pages
// count calls identically. A call is attributed to an agent as soon as
// Smartflo names one on the leg, even if that leg never connected (e.g. a
// click-to-call attempt) — so an agent's totalCalls can exceed their
// answered count. Sorted by answered desc (top performer first).
//
// `calls` arrives newest-first (server returns ORDER BY id DESC), which
// matters here: a phone number can be reassigned to a different agent over
// time, so the display name is taken only from the first (newest) call seen
// per key and never overwritten by older calls sharing that number —
// otherwise a stale, previously-retired agent's name would win over the
// current one for that number's entire all-time total.
export const buildAgentStats = (calls) => {
  const byAgent = new Map();

  for (const call of calls) {
    const key = call.answered_agent_number || call.answered_agent_name;
    if (!key) continue;

    let agent = byAgent.get(key);
    if (!agent) {
      agent = {
        key,
        name: call.answered_agent_name || call.answered_agent_number,
        number: call.answered_agent_number || '-',
        totalCalls: 0,
        answered: 0,
        totalBillsec: 0,
      };
      byAgent.set(key, agent);
    }

    agent.totalCalls += 1;

    if (getCallStatus(call) === 'Answered') {
      agent.answered += 1;
      const billsec = Number(call.billsec);
      if (!Number.isNaN(billsec)) agent.totalBillsec += billsec;
    }
  }

  return Array.from(byAgent.values()).sort((a, b) => b.answered - a.answered);
};

// Distinct agents seen across all calls (name + number), for populating the
// "call as" picker in the click-to-call button. Newest-seen name wins for a
// given number, same tie-breaking rule as buildAgentStats in AgentDetails -
// `calls` arrives newest-first, so only the first (newest) call seen per
// number is kept; a later, older call reusing that number must not overwrite it.
export const getKnownAgents = (calls) => {
  const byNumber = new Map();
  for (const call of calls) {
    if (!call.answered_agent_number) continue;
    if (!byNumber.has(call.answered_agent_number)) {
      byNumber.set(call.answered_agent_number, {
        number: call.answered_agent_number,
        name: call.answered_agent_name || call.answered_agent_number,
      });
    }
  }
  return Array.from(byNumber.values()).sort((a, b) => a.name.localeCompare(b.name));
};

// How many times in a row the customer's most recent status has repeated,
// newest call first. Resets to 1 the moment the status type changes at all —
// not just on Answered — so a fresh inbound Missed call right after an
// Attempted callback (or vice versa) doesn't inherit the other one's count.
export const getUnansweredStreak = (group) => {
  const latestStatus = getCallStatus(group.latestCall);
  if (latestStatus === 'Answered') return 0;
  let streak = 0;
  for (const call of group.callsNewestFirst) {
    if (getCallStatus(call) !== latestStatus) break;
    streak += 1;
  }
  return streak;
};

// A customer becomes Critical once they've been missed 3 or more times in a
// row with no answered call in between (getUnansweredStreak already resets on
// any status change, so this can't be inflated by older, unrelated calls).
// Scoped to 'Missed' specifically — an Attempted streak (agent callbacks not
// picked up) is a different problem and shouldn't trigger this flag.
export const CRITICAL_MISSED_THRESHOLD = 3;

export const isCritical = (group) => {
  const latestStatus = getCallStatus(group.latestCall);
  if (latestStatus !== 'Missed') return false;
  return getUnansweredStreak(group) >= CRITICAL_MISSED_THRESHOLD;
};
